import React from 'react';
import { Box, Card, CardContent, Chip, Divider } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, PageHeader } from '@/components/ui';
import {
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
  WebhookOutlined,
} from '@mui/icons-material';
import { WEBHOOK_STATUSES } from '../../constants/webhook-status';
import type { Webhook } from '../../types/webhook';

// Mock data for demonstration
const mockWebhook: Webhook = {
  transaction_id: 'txn_987654321',
  merchant_transaction_id: 'merch_123456789',
  transaction_status: 'completed',
  webhook_status: 'DELIVERED',
  created_at: '2024-01-15T10:30:00Z',
  updated_at: '2024-01-15T10:30:00Z',
  webhook_url: 'https://api.example.com/webhooks',
  retry_count: 0,
  response_code: 200,
  response_body: JSON.stringify({
    success: true,
    message: 'Webhook processed successfully',
  }),
};

const WebhookDetailPage: React.FC = () => {
  const { webhookId } = useParams<{ webhookId: string }>();
  const navigate = useNavigate();

  // In real implementation, fetch webhook data using webhookId
  const webhook = mockWebhook;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case WEBHOOK_STATUSES.DELIVERED:
        return <CheckCircleOutline sx={{ color: '#10B981' }} />;
      case WEBHOOK_STATUSES.FAILURE:
        return <ErrorOutline sx={{ color: '#EF4444' }} />;
      case WEBHOOK_STATUSES.PENDING:
        return <HourglassEmpty sx={{ color: '#F59E0B' }} />;
      default:
        return <WebhookOutlined />;
    }
  };

  const getStatusColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case WEBHOOK_STATUSES.DELIVERED:
        return 'success';
      case WEBHOOK_STATUSES.FAILURE:
        return 'error';
      case WEBHOOK_STATUSES.PENDING:
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleRetryWebhook = () => {
    // Implementation would trigger webhook retry
  };

  const WebhookIcon = WebhookOutlined;

  return (
    <Box className="fade-in">
      <PageHeader
        title="Webhook Details"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Webhooks', href: '/webhooks' },
          { label: `Webhook ${webhook.transaction_id}` },
        ]}
        showDivider={false}
      />

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
          {/* Main Details Card */}
          <Box sx={{ flex: 2 }}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={3}>
                  <WebhookIcon color="primary" sx={{ mr: 2, fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="600">
                    Webhook Information
                  </Typography>
                </Box>

                <Box display="flex" flexDirection="column" gap={2.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Transaction ID
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {webhook.transaction_id}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Webhook URL
                    </Typography>
                    <Typography variant="body2" fontWeight="500" sx={{ wordBreak: 'break-all' }}>
                      {webhook.webhook_url}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Status
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getStatusIcon(webhook.webhook_status)}
                      <Chip
                        label={
                          webhook.webhook_status.charAt(0).toUpperCase() +
                          webhook.webhook_status.slice(1)
                        }
                        color={getStatusColor(webhook.webhook_status)}
                        size="small"
                        variant="filled"
                      />
                    </Box>
                  </Box>

                  <Divider />

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      HTTP Status
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="500"
                      sx={{
                        color:
                          webhook.response_code &&
                          webhook.response_code >= 200 &&
                          webhook.response_code < 300
                            ? 'success.main'
                            : 'error.main',
                      }}
                    >
                      {webhook.response_code || 'N/A'}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Retry Count
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {webhook.retry_count || 0}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Created
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {new Date(webhook.created_at).toLocaleString()}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Last Updated
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {new Date(webhook.updated_at).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Side Info Card */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="600" mb={3}>
                  Quick Actions
                </Typography>

                <Box display="flex" flexDirection="column" gap={2}>
                  {webhook.webhook_status === WEBHOOK_STATUSES.FAILURE && (
                    <button
                      onClick={handleRetryWebhook}
                      style={{
                        width: '100%',
                        backgroundColor: '#3B82F6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      Retry Webhook
                    </button>
                  )}

                  <button
                    onClick={() => navigate('/webhooks')}
                    style={{
                      width: '100%',
                      backgroundColor: 'transparent',
                      color: '#6B7280',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      padding: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                  >
                    Back to Webhooks
                  </button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Additional Details */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
            {/* Request Payload */}
            <Box sx={{ flex: 1 }}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="600" mb={2}>
                    Request Payload
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#F8F9FA',
                      borderRadius: 1,
                      p: 2,
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      overflow: 'auto',
                      maxHeight: '300px',
                    }}
                  >
                    <pre>
                      {JSON.stringify(
                        {
                          transaction_id: webhook.transaction_id,
                          merchant_transaction_id: webhook.merchant_transaction_id,
                          transaction_status: webhook.transaction_status,
                        },
                        null,
                        2
                      )}
                    </pre>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Response/Error */}
            <Box sx={{ flex: 1 }}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="600" mb={2}>
                    {webhook.error_message ? 'Error Details' : 'Response Body'}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: webhook.error_message ? '#FEF2F2' : '#F8F9FA',
                      borderRadius: 1,
                      p: 2,
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      overflow: 'auto',
                      maxHeight: '300px',
                    }}
                  >
                    {webhook.error_message ? (
                      <Typography variant="body2" sx={{ color: 'error.main' }}>
                        {webhook.error_message}
                      </Typography>
                    ) : webhook.response_body ? (
                      <pre>{webhook.response_body}</pre>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No response data available
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WebhookDetailPage;
